import { Alert, Button, Divider, Stack } from "@mui/material";
import { FormApi } from "final-form";
import { Switches } from "mui-rff";
import { Form, useField } from "react-final-form";

import { useDashboardPublicWarningSettingsQuery } from "@/features/admin/features/dashboards/hooks/queries";
import { useUpdateDashboardPublicWarningSettingsMutation } from "@/features/admin/features/dashboards/hooks/mutations";
import { WarningPageStaticOverlays } from "@/features/admin/features/dashboards/components/warning-pages-settings/warning-page-static-overlays";
import { useAdminDashboard } from "@/features/admin/hooks";
import { showToast, TextField } from "@/features/shared/components";
import {
  usePublicWarningPageDefaults,
  useToolbarLabelWithCode,
} from "@/features/shared/hooks/use-warning-page-defaults";
import type { StaticMapOverlaysOverlayConfiguration } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import {
  readPublicWarningPageSettings,
  publicWarningSettingsUpdateBody,
} from "@/lib/public-warning-settings.utils";

interface WarningPageSettingsFields {
  enabled?: boolean;
  title?: string;
  description?: string;
  toolbarLabel?: string;
  staticOverlays?: StaticMapOverlaysOverlayConfiguration[];
}

function WarningPageFields() {
  const { t } = useTranslation();
  const toolbarLabel = useField<string | undefined>("toolbarLabel", {
    subscription: { value: true },
  }).input.value;
  const withCode = useToolbarLabelWithCode();

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Switches
        name="enabled"
        data={{
          label: t("admin.dashboard_settings.warning_pages.enabled"),
          value: true,
        }}
      />
      <TextField
        name="title"
        label={t("admin.dashboard_settings.warning_pages.fields.title")}
        placeholder={t("riskPicture.defaults.title")}
      />
      <TextField
        name="description"
        label={t("admin.dashboard_settings.warning_pages.fields.description")}
        placeholder={t("riskPicture.defaults.description")}
        multiline
        minRows={2}
      />
      <TextField
        name="toolbarLabel"
        label={t("admin.dashboard_settings.warning_pages.fields.alert_button_label")}
        placeholder={t("riskPicture.defaults.toolbar")}
        helperText={t("admin.dashboard_settings.warning_pages.fields.alert_button_help", {
          example: withCode(
            toolbarLabel || t("riskPicture.defaults.toolbar"),
            `${t("common.code")} ${t("code.orange")}`,
          ),
        })}
      />
    </Stack>
  );
}

function WarningPageForm() {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const settingsQuery = useDashboardPublicWarningSettingsQuery(dashboard?.id ?? "");
  const updateSettings = useUpdateDashboardPublicWarningSettingsMutation();
  const settings = settingsQuery.data;
  const defaults = usePublicWarningPageDefaults();
  const page = readPublicWarningPageSettings(settings, defaults);

  if (settingsQuery.isError) {
    return (
      <Alert severity="error">{t("admin.dashboard_settings.warning_pages.error")}</Alert>
    );
  }
  if (!dashboard || settingsQuery.isLoading || !settings) return null;

  const initialValues: WarningPageSettingsFields = {
    enabled: page.enabled,
    title: page.title,
    description: page.description,
    toolbarLabel: page.toolbarLabel,
    staticOverlays: page.staticOverlays,
  };

  const onSubmit = (
    values: WarningPageSettingsFields,
    form: FormApi<WarningPageSettingsFields>,
  ) => {
    updateSettings.mutate(
      {
        dashboardId: dashboard.id,
        body: publicWarningSettingsUpdateBody(
          {
            enabled: values.enabled === true,
            title: values.title,
            description: values.description,
            toolbarLabel: values.toolbarLabel,
            staticOverlays: values.staticOverlays,
          },
          defaults,
        ),
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.dashboard_updated"));
          form.reset(values);
        },
        onError: () => {
          showToast(t("admin.dashboard_settings.warning_pages.error"));
        },
      },
    );
  };

  return (
    <Stack spacing={1}>
      <Form<WarningPageSettingsFields>
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, pristine }) => (
          <Stack spacing={2}>
            <form id="warning-page-settings" onSubmit={handleSubmit}>
              <WarningPageFields />
            </form>
            <Divider />
            {/* Outside <form>: the overlay modals submit their own nested forms. */}
            <WarningPageStaticOverlays />
            <Stack alignItems="flex-end">
              <Button
                type="submit"
                form="warning-page-settings"
                variant="outlined"
                disabled={pristine || updateSettings.isPending}
              >
                {t("admin.dashboard_settings.warning_pages.save")}
              </Button>
            </Stack>
          </Stack>
        )}
      />
    </Stack>
  );
}

const WarningPagesSettings = () => (
  <Stack spacing={4} sx={{ py: 1 }}>
    <WarningPageForm />
  </Stack>
);

export default WarningPagesSettings;
